package signup

import apiclient.passwords.PasswordEvaluation
import dev.fritz2.components.compat.a
import dev.fritz2.components.compat.div
import dev.fritz2.components.compat.span
import dev.fritz2.components.lineUp
import dev.fritz2.core.RenderContext
import dev.fritz2.core.SimpleHandler
import dev.fritz2.core.href
import dev.fritz2.core.target
import dev.fritz2.core.title
import dev.fritz2.styling.params.FlexParams
import dev.fritz2.styling.params.ScaledValueProperty
import dev.fritz2.styling.params.Style
import koin.koinCtx
import kotlinx.coroutines.flow.Flow
import mainmenu.RouterStore
import model.ValidatePassword
import theme.FormationColors
import twcomponents.twRowOfJustifyBetween

fun RenderContext.inputTitle(text: Flow<String>) {
    span(
        {
            fontSize { smaller }
            fontWeight { lighter }
            color { FormationColors.GrayDisabled.color }
            margins {
                left { normal }
            }
        },
    ) { text.renderText(into = this) }
}

fun RenderContext.twTextButton(
    text: Flow<String>,
    additionalClasses: String? = null,
    routingMap: Map<String, String>? = null,
    replaceRoute: Map<String, String>? = null,
    attributes: List<Pair<String, String>>? = null,
    clickHandlers: List<SimpleHandler<Unit>>? = null,
) {
    val routerStore: RouterStore by koinCtx.inject()

    button("h-min hover:text-highlight mx-1 underline") {
        additionalClasses?.let { className(it) }
        attributes?.forEach { (key, value) ->
            attr(key, value)
        }
        text.renderText(into = this)

        clicks handledBy {
            clickHandlers?.forEach { handler ->
                handler(Unit)
            }
            replaceRoute?.let { route ->
                routerStore.addOrReplaceRoute(route)
            }
            routingMap?.let { route ->
                routerStore.validateInternalRoute(route)
            }
        }
    }
}

fun RenderContext.textLinkButton(
    text: Flow<String>,
    fontSize: ScaledValueProperty = { normal },
    link: String,
    attributes: List<Pair<String, String>>? = null,
    addStyling: Style<FlexParams>? = null
) {
    a(
        {
            height { minContent }
            hover {
                color { FormationColors.GreenBright.color }
            }
            addStyling?.let { it() }
        },
    ) {
        href(link)
        target("_blank")
        attributes?.forEach { (key, value) ->
            attr(key, value)
        }
        span(
            {
                fontSize(fontSize)
                textDecoration { underline }
            },
        ) {
            text.renderText(into = this)
        }
    }
}

fun RenderContext.passwordStrengthIndicator(result: Flow<ValidatePassword>) {

    result.render { pwStack ->
        twRowOfJustifyBetween {
            className("px-4")
            if (pwStack.firstPass.isNotBlank()) {
                val eval = pwStack.passwordEval
                if (eval != null) {
                    val color = when (eval) {
                        PasswordEvaluation.Unacceptable -> FormationColors.RedError.color
                        PasswordEvaluation.Weak -> FormationColors.OrangeCustom.color
                        PasswordEvaluation.Acceptable -> FormationColors.BlueMarine.color
                        PasswordEvaluation.Strong -> FormationColors.GreenForest.color
                        else -> FormationColors.RedError.color
                    }
                    val number = when (eval) {
                        PasswordEvaluation.Unacceptable -> 0
                        PasswordEvaluation.Weak -> 1
                        PasswordEvaluation.Acceptable -> 2
                        PasswordEvaluation.Strong -> 3
                        else -> 0
                    }

                    lineUp {
                        spacing { tiny }
                        items {
                            for (i in 1..number) {
                                div(
                                    {
                                        width { "40px" }
                                        height { "5px" }
                                        radius { full }
                                        background { color { color } }
                                    },
                                ) {}
                            }
                        }
                    }
                    span(
                        {
                            height { normal }
                            fontSize { tiny }
                            color { color }
                        },
                    ) {
                        title(eval.description)
                        +eval.name
                    }
                } else {
                    span({ height { normal } }) { +"" } // Placeholder
                }
            } else {
                span({ height { normal } }) { +"" } // Placeholder
            }
        }
    }
}
